// Wheel-driven zoom + horizontal-pan for the timeline scroll wrap. Mounts
// a `wheel` listener with `{ passive: false }` so we can preventDefault
// when (and ONLY when) the user actually wants the timeline to react —
// otherwise plain wheel events pass through and the page scrolls normally.
//
// Gestures:
//   - Cmd/Ctrl + wheel  → zoom in/out around the cursor (anchored).
//                         macOS trackpad pinch arrives as wheel events
//                         with `ctrlKey=true` synthetically, so it Just
//                         Works alongside the modifier-key path.
//   - Shift + wheel     → horizontal pan on the timeline. Useful when
//                         manually zoomed past show-all and the chip the
//                         user wants is off-screen.
//   - Plain wheel       → no preventDefault → browser scrolls the page.
//                         Eliminates the "cursor accidentally over the
//                         timeline scroll-traps the page" complaint.
//
// Zoom step uses an exponential factor `pow(1.0015, -deltaY)` so the
// curve is smooth across both wheel-clicks (deltaY ≈ ±100 → ±15%) and
// trackpad smooth-scrolling (deltaY ≈ ±5 → ±0.75%). Same shape Figma /
// Tldraw / Excalidraw use.
//
// Cursor-anchor: the zoom math is identical to useTimelineZoom.zoomFitTo's
// `anchorClientX` branch — capture the timeline-time at the cursor BEFORE
// the zoom commits, then set scrollLeft so the same time lands at the
// same viewport-X afterwards.

import { useEffect, useRef, type RefObject } from 'react';
import { flushSync } from 'react-dom';

interface Args {
  scrollWrapRef: RefObject<HTMLDivElement | null>;
  /** Current displayed pxPerSec — drives the cursor-anchor math. */
  pxPerSec: number;
  /** "Show all" floor — zoom-out below this snaps to sticky-fit (-1). */
  fitPxPerSec: number;
  /** Hard cap on manual zoom-in. */
  pxPerSecMax: number;
  /** Setter — pass `-1` to engage sticky-fit, any other number to manual. */
  setPxPerSecManual: (n: number) => void;
}

/** Smaller = slower zoom, larger = faster. 0.0015 lands ~15% per wheel
 *  click and ~0.75% per fine trackpad event — same feel as Figma. */
const ZOOM_RATE = 0.0015;

export function useTimelineWheel({
  scrollWrapRef,
  pxPerSec,
  fitPxPerSec,
  pxPerSecMax,
  setPxPerSecManual,
}: Args): void {
  // Ref-mirror every dependency so the wheel listener can be bound ONCE
  // on mount and read fresh values on each event. Without this, every
  // pxPerSec change (which fires every wheel tick during zoom!) would
  // unbind + re-bind the listener and add overhead in the hot loop.
  const pxPerSecRef = useRef(pxPerSec);
  pxPerSecRef.current = pxPerSec;
  const fitPxPerSecRef = useRef(fitPxPerSec);
  fitPxPerSecRef.current = fitPxPerSec;
  const setPxPerSecManualRef = useRef(setPxPerSecManual);
  setPxPerSecManualRef.current = setPxPerSecManual;
  const pxPerSecMaxRef = useRef(pxPerSecMax);
  pxPerSecMaxRef.current = pxPerSecMax;

  useEffect(() => {
    const wrap = scrollWrapRef.current;
    if (!wrap) return;

    const onWheel = (e: WheelEvent) => {
      const cmdOrCtrl = e.ctrlKey || e.metaKey;

      if (cmdOrCtrl) {
        // Zoom path — preventDefault so the browser doesn't also page-zoom
        // (Cmd+wheel is the browser's native zoom shortcut on Chromium).
        e.preventDefault();
        const cur = pxPerSecRef.current;
        if (cur <= 0) return;
        const wrapRect = wrap.getBoundingClientRect();
        const cursorX = e.clientX - wrapRect.left;
        // Capture the timeline-time the cursor is currently sitting on so
        // we can re-pin it at the same viewport-X after the zoom commits.
        const cursorTimeSec = (cursorX + wrap.scrollLeft) / cur;

        // Negative deltaY = wheel-up / pinch-out → zoom in.
        const factor = Math.pow(1 + ZOOM_RATE, -e.deltaY);
        const requested = cur * factor;
        const fit = fitPxPerSecRef.current;
        const max = pxPerSecMaxRef.current;
        const clamped = Math.max(fit, Math.min(max, requested));
        if (Math.abs(clamped - cur) < 0.01) return;

        flushSync(() => {
          // At-or-below show-all → engage sticky-fit (sentinel -1) so the
          // zoom hook's "auto re-fit on totalDur change" path stays alive.
          if (clamped <= fit + 0.01) {
            setPxPerSecManualRef.current(-1);
          } else {
            setPxPerSecManualRef.current(clamped);
          }
        });

        // Anchor scrollLeft so cursorTimeSec lands at cursorX again.
        const target = cursorTimeSec * clamped - cursorX;
        const maxScroll = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
        wrap.scrollLeft = Math.max(0, Math.min(maxScroll, target));
      } else if (e.shiftKey) {
        // Shift+wheel → horizontal pan. Only meaningful when the track
        // is wider than the wrap; otherwise scrollLeft assignment is
        // clamped to 0 by the browser and this is a no-op (still
        // preventDefault to silence the otherwise-rejected vertical
        // page-scroll attempt that some browsers fall back to).
        //
        // Browser quirk: Chrome/Edge on macOS+Windows auto-remap Shift+
        // wheel from deltaY → deltaX, while Firefox keeps it on deltaY.
        // Read whichever axis arrived non-zero so the gesture works
        // everywhere; a true two-finger trackpad swipe also lands here
        // with deltaX populated and works the same way.
        e.preventDefault();
        const delta = e.deltaX || e.deltaY;
        wrap.scrollLeft += delta;
      }
      // Plain wheel: no preventDefault → page scrolls normally. THIS is
      // the line that solves the "cursor over timeline traps the page"
      // problem — we only intercept when a modifier signals intent.
    };

    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      wrap.removeEventListener('wheel', onWheel);
    };
    // Stable deps: only the scrollWrap ref. The pxPerSec / fitPxPerSec /
    // setter values are read via refs above so we don't churn the listener.
  }, [scrollWrapRef]);
}
